import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { IconsComponent } from '@sml/icons';
import {
  FileService,
  IFileModel,
  handleFile,
  convertBytesToMB,
  getFileSizeInMB,
  ACCEPTED_FILE_TYPES,
  FILE_TYPE,
  DOC_TYPE,
  getIconName,
  getCompressionQuality,
  IFileModelMediaUploader,
  FilesModel,
  ImageCompressServiceWrapper,
  TooltipDirective,
  DOWNLOAD_INPROGRESS,
} from '@sml/utility';
import {
  MAX_TOTAL_FILE_SIZE,
  MEDIA_UPLOADER_STRINGS,
} from './media-uploader.constant';
import { ImagePreview } from './media-uploader.model';
import { SpinnerComponent } from '@sml/spinner';
import { ToastrService } from '@sml/toastr';

@Component({
  selector: 'sml-media-uploader',
  templateUrl: './media-uploader.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IconsComponent, DragDropModule, SpinnerComponent, TooltipDirective],
})
export class MediaUploaderComponent implements OnInit, OnChanges {
  @Input() isFromCompleteCampaignDialog: boolean = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() allowedFileTypes = ACCEPTED_FILE_TYPES;
  @Input() maximumSize = MAX_TOTAL_FILE_SIZE;
  @Input() set mediaFiles(data: IFileModel[]) {
    if (data?.length) {
      this.initializeImages(data);
    }
  }
  @Input() set docFiles(datadoc: IFileModel[]) {
    if (datadoc?.length) {
      this.initializeDocs(datadoc);
    }
  }
  @Output() isFileUploaded = new EventEmitter<FilesModel>();
  @Output() galleryFilesLength = new EventEmitter<number>();
  @Output() documentFilesLength = new EventEmitter<number>();
  @Output() allowTabSwitch = new EventEmitter<boolean>();
  @Input() inDonorTable = false
  @Input() isRecordPayment = false
  @Input() fromCampaignDesign=false;
  @Input() isCampaignDocument = false
  @Input() fromActivitylog = false;
  @Input() imageSize:string='';
  oneFileOnly = input<boolean>(false);
  strings = MEDIA_UPLOADER_STRINGS;
  isGalleryView = false;
  isOverLimit = false;
  isInvalidFile = false;
  isTabSwitch = false;
  isGalleryViewError = '';
  isDocViewError = '';
  isGalleryInvalidError = '';
  isDocInvalidError = '';
  count = 0;
  numberOfFilesUploded = 0;
  acceptedFileTypes!: string[];
  imagePreviewListBlob: IFileModelMediaUploader[] = [];
  galleryFiles: IFileModelMediaUploader[] = [];
  documentFiles: IFileModelMediaUploader[] = [];
  docExtensions: string[] = Object.values(DOC_TYPE) as string[];
  imageExtensions: string[] = Object.values(FILE_TYPE) as string[];

  constructor(
    private readonly fileService: FileService,
    private readonly cdr: ChangeDetectorRef,
    private readonly alertService: ToastrService,
    private readonly convertUrlService: FileService,
    private readonly imageCompressor: ImageCompressServiceWrapper,
    private readonly toastrService: ToastrService,
  ) { }


  ngOnInit(): void {
    this.isTabSwitch = true;
    this.allowTabSwitch.emit(true);
  }

  ngOnChanges(): void {
    this.updateAcceptedFileTypes();
    this.imagePreviewListBlob = [
      ...(this.isGalleryView ? this.galleryFiles : this.documentFiles),
      ...(this.imagePreviewListBlob || []).filter(o => o.tempId),
    ];
    if (this.count === 0) {
      this.isTabSwitch = true;
      this.allowTabSwitch.emit(true);
    }
  }

  emitFileLengths(): void {
    this.galleryFilesLength.emit(this.galleryFiles.length);
    this.documentFilesLength.emit(this.documentFiles.length);
  }

  updateAcceptedFileTypes(): void {
    this.isGalleryView = this.allowedFileTypes === ACCEPTED_FILE_TYPES;

    this.acceptedFileTypes = this.isGalleryView
      ? Object.keys(FILE_TYPE)
      : Object.keys(DOC_TYPE);
  }

  getIconNames(extension: string): string {
    return getIconName(extension);
  }

  initializeImages(data: IFileModel[]): void {
    const newImages: IFileModelMediaUploader[] = data.map((image) => ({
      ...image,
      size: this.formatFileSize(image.Size),
    }));

    this.galleryFiles = newImages;


    this.emitFileLengths();
  }

  initializeDocs(data: IFileModel[]): void {
    const newImagesDocument: IFileModelMediaUploader[] = data.map((image) => ({
      ...image,
      size: this.formatFileSize(image.Size),
    }));
    this.documentFiles = newImagesDocument;


    this.emitFileLengths();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if(this.oneFileOnly() && ((files && files.length > 1) || (this.imagePreviewListBlob.length > 0)))
      {
        this.alertService.error({ message: "Please select a single photo to be uploaded. In case you want to select another photo, delete the current photo to select a new one", icon: 'warning_o', delay: 5000 });
        this.fileInput.nativeElement.value = '';
        return;
      }
    if (files && files.length > 0) {
      this.processFiles(Array.from(files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  handleFileInput(event: Event): void {
  
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if(this.oneFileOnly() && ((files && files.length > 1) || (this.imagePreviewListBlob.length > 0)))
      {
        this.alertService.error({ message: "Please select a single photo to be uploaded. In case you want to select another photo, delete the current photo to select a new one", icon: 'warning_o', delay: 5000 });
        this.fileInput.nativeElement.value = '';
        return;
      }
    if (files && files.length > 0) {
      this.processFiles(Array.from(files));
      this.fileInput.nativeElement.value = '';
    }
  }

  processFiles(files: File[]): void {
    if (!this.validateFiles(files)) return;
    this.count = 0;
    this.numberOfFilesUploded = files.length;
    this.isTabSwitch = false;
    this.allowTabSwitch.emit(this.isTabSwitch);
    this.isInvalidFile = false;
    files.forEach((file) => {
      const tempPreview = this.createFilePreview(file);
      this.addToImagePreviewList(tempPreview);
      if (file.type.startsWith('image/')) {
        this.compressAndUploadImage(file, tempPreview.tempId);
      } else {
        this.uploadWithoutCompressing(file, tempPreview.tempId);
      }
    });
    this.cdr.detectChanges();
  }

  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
      .replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0,
          v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
  }

  private createFilePreview(file: File): IFileModelMediaUploader {
    return {
      Id: '',
      Name: file.name,
      ContainerName: '',
      ContentType: file.type,
      Size: 0,
      Extension: file.name.split('.').pop()?.toLowerCase() || '',
      BlobUrl: URL.createObjectURL(file),
      SeqNumber: this.imagePreviewListBlob.length + 1,
      size: '',
      isTemporaryUrl: true,
      tempId: this.uuidv4()
    };
  }
  downloadImage(url: string, fileName: string) {
    this.toastrService.success({
      message: DOWNLOAD_INPROGRESS,
      icon: 'tick_o',
    });
    this.convertUrlService.convertUrl(url).subscribe((base64Data: string) => {
      if (base64Data) {
        const a = document.createElement('a');
        a.href = `data:application/octet-stream;base64,${base64Data}`;
        a.download = fileName;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    });
  }

  addToImagePreviewList(filePreview: IFileModelMediaUploader): void {

      this.imagePreviewListBlob = [...this.imagePreviewListBlob, filePreview];


  }

  updateFileSizeInPreviewList(fileName: string, newSize: number): void {
    const fileIndex = this.imagePreviewListBlob.findIndex(file => file.Id === '' && file.Name === fileName);
    if (fileIndex !== -1) {
      this.imagePreviewListBlob[fileIndex].Size = newSize;
      this.imagePreviewListBlob[fileIndex].size = this.formatFileSize(newSize);
    }
  }

  async compressAndUploadImage(file: File, tempId?: string): Promise<void> {
    const compressedFile = await this.imageCompressor.compressImage(file);
    this.updateFileSizeInPreviewList(file.name, compressedFile.size);
    if (!this.validateFiles([compressedFile])) {
      this.removeImage(this.imagePreviewListBlob.length - 1);
    } else {
      setTimeout(() => {
        const formData = new FormData();
        formData.append('files', compressedFile);
        this.uploadFile(formData, tempId);
      }, 0);
    }
   
  }

  uploadWithoutCompressing(file: File, tempId?: string): void {
    this.updateFileSizeInPreviewList(file.name, file.size);
    if (!this.validateFiles([file])) {
      this.removeImage(this.imagePreviewListBlob.length - 1);
    }
    else{
      setTimeout(() => {
        const formData = new FormData();
       formData.append('files', file);
       this.uploadFile(formData, tempId);
      }, 0);
    }
    
  }

  uploadFile(formData: FormData, tempId?: string): void {
    this.fileService.uploadFile(formData, 'file', !this.isRecordPayment).subscribe({
      next: (resp: string | null) => {
        if (resp) {
          const blob: IFileModel[] = JSON.parse(resp);
          if (blob?.length) {
            this.count++;
            this.handleUploadSuccess(blob, tempId);
          }
          this.isTabSwitch = this.count === this.numberOfFilesUploded;
          this.allowTabSwitch.emit(this.isTabSwitch);
        }
      },
    });
  }

  validateFiles(files: File[]): boolean {
    if (this.hasInvalidFiles(files)) return false;
    const totalSize = this.calculateTotalSize(files);
    const remainingSize = this.getRemainingSize(files);
    if (this.isExceedingSizeLimit(totalSize)) {
      this.setOverLimitErrorMessage(remainingSize);
      return false;
    }
    return true;
  }

  hasInvalidFiles(files: File[]): boolean {
    if (files.some((file) => !handleFile(file, this.isGalleryView))) {
      this.isInvalidFile = true;
      const extensions = this.getAllowedExtensions();
      const invalidErrorMessage = this.strings.INVALID_FILE_ERROR.replace(
        '{extensions}',
        extensions,
      );
      this.setInvalidFileErrorMessage(invalidErrorMessage);
      return true;
    }
    return false;
  }

  getAllowedExtensions(): string {
    return this.isGalleryView
      ? this.strings.GALLERY_UPLOAD_FILE_TYPE
      : this.strings.DOCUMENT_UPLOAD_FILE_TYPE;
  }

  setInvalidFileErrorMessage(message: string): void {
    this.alertService.error({ message: message, icon: 'warning_o', delay: 5000 });
  }

  calculateTotalSize(newFiles: File[]): number {
    const currentTotalSize = this.getCurrentTotalSize();
   
    return currentTotalSize
  }

  getCurrentTotalSize(): number {
    return convertBytesToMB(
      this.imagePreviewListBlob.reduce((total, img) => total + img.Size, 0),
    );
  }

  getNewFilesTotalSize(newFiles: File[]): number {
    return convertBytesToMB(
      newFiles.reduce((total, file) => total + file.size, 0),
    );
  }

  getRemainingSize(newFiles: File[]): number {
    const usedSize = this.imagePreviewListBlob.reduce(
      (total, img) => total + convertBytesToMB(img.Size),
      0,
    );
    const newFileSize = this.getNewFilesTotalSize(newFiles);
    const remainingSize = usedSize-newFileSize;
    return parseFloat((Math.max(this.maximumSize - remainingSize, 0)).toFixed(1));
  }

  isExceedingSizeLimit(totalSize: number): boolean {
    this.isOverLimit = totalSize > this.maximumSize;
    return this.isOverLimit;
  }

  setOverLimitErrorMessage(remainingSize: number): void {
    const errorMessage = this.strings.UPLOAD_ERROR.replace(
      '{maximumSize}',
      `${remainingSize}`,
    );
    this.alertService.error({ message: errorMessage, icon: 'warning_o', delay: 5000, });
  }

  handleUploadSuccess(blob: IFileModel[], tempId?: string): void {
    if (this.isGalleryView) {
      const validImages = blob.filter((file) => this.imageExtensions.includes(file.Extension?.toLocaleLowerCase())).map((response) => ({ ...response, size: this.formatFileSize(response.Size), }));
      this.galleryFiles = [...this.galleryFiles, ...validImages];
      this.imagePreviewListBlob = this.imagePreviewListBlob.filter(i => i.tempId !== tempId);
      this.updateSequenceNumbers();
      this.emitFileLengths();
      this.isFileUploaded.emit({
        galleryFiles: this.galleryFiles.map(({ size, ...rest }) => rest),
      });
    } else {
      const validDocs = blob
        .filter((file) =>
          [...this.docExtensions, ...this.imageExtensions].includes(
            file.Extension.toLowerCase(),
          ),
        )
        .map((response) => ({
          ...response,
          size: this.formatFileSize(response.Size),
        }));
      this.documentFiles = [...this.documentFiles, ...validDocs];
      this.imagePreviewListBlob = this.imagePreviewListBlob.filter(i => i.tempId !== tempId);
      this.updateSequenceNumbers();
      this.emitFileLengths();
      this.isFileUploaded.emit({
        documentFiles: this.documentFiles.map(({ size, ...rest }) => rest),
      });
    }
    this.cdr.detectChanges();
  }

  removeImage(index: number): void {
    if (this.isGalleryView) {
      this.galleryFiles.splice(index, 1);
      this.imagePreviewListBlob = [...this.galleryFiles];
      this.isFileUploaded.emit({
        galleryFiles: this.galleryFiles.map(({ size, ...rest }) => rest),
      });
    } else {
      this.documentFiles.splice(index, 1);
      this.imagePreviewListBlob = [...this.documentFiles];
      this.isFileUploaded.emit({
        documentFiles: this.documentFiles.map(({ size, ...rest }) => rest),
      });
    }
    this.emitFileLengths();
    this.resetErrorsAndFlags();
    this.cdr.detectChanges();
  }

  resetErrorsAndFlags(): void {
    this.isOverLimit = false;
    this.isGalleryViewError = '';
    this.isDocViewError = '';
    this.isGalleryInvalidError = '';
    this.isDocInvalidError = '';
    this.isInvalidFile = false;
  }

  drop(event: CdkDragDrop<ImagePreview[]>): void {
    moveItemInArray(this.imagePreviewListBlob, event.previousIndex, event.currentIndex,);
    if (this.isGalleryView) {
      moveItemInArray(this.galleryFiles, event.previousIndex, event.currentIndex,);
      this.updateSequenceNumbers();
      this.isFileUploaded.emit({
        galleryFiles: this.galleryFiles.map(({ size, ...rest }) => rest),
      });
    } else {
      moveItemInArray(this.documentFiles, event.previousIndex, event.currentIndex,);
      this.updateSequenceNumbers();
      this.isFileUploaded.emit({
        documentFiles: this.documentFiles.map(({ size, ...rest }) => rest),
      });
    }
  }

  updateSequenceNumbers(): void {
    this.imagePreviewListBlob = this.imagePreviewListBlob.map((image, index) => ({ ...image, SeqNumber: index + 1, }),);
    if (this.isGalleryView) {
      this.galleryFiles = this.galleryFiles.map((image, index) => ({ ...image, SeqNumber: index + 1, }));
    } else {
      this.documentFiles = this.documentFiles.map((image, index) => ({ ...image, SeqNumber: index + 1, }));
    }
  }

  formatFileSize(size: number): string {
    return getFileSizeInMB(size);
  }
}
